import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Check, X } from 'lucide-react';
import { createUser, checkUsernameAvailable, checkEmailAvailable, createOrLinkProfile } from '@/lib/api/users';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { fetchCompanies } from '@/lib/api/index';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { MASTER_TENANT_ID } from '@/types/tenant';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImageUpload } from '@/components/ui/image-upload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ORGANISATION_ROLE_LABELS,
  useOrganisation,
  type OrganisationRole,
} from '@/contexts/OrganisationContext';

type UserRole = 'admin' | 'tenant_admin' | 'company_admin' | 'user';

export default function CreateUser() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCompanyId = searchParams.get('company') || undefined;
  const { user: currentUser, hasRole } = useAuth();
  const { isMasterTenant, tenant } = useTenant();
  const {
    hasEnterpriseHierarchy,
    effectiveRole,
    activeAssignment,
    activeUnitId,
    units: organisationUnits,
  } = useOrganisation();
  
  // Check if current user is a tenant super admin (admin role on white-label tenant)
  const isTenantSuperAdmin = !isMasterTenant && hasRole('admin');
  
  // Use domain's tenant_id for filtering (NOT user's profile tenant_id)
  // This ensures GSK domain only sees GSK companies, even if user has different profile tenant
  const effectiveTenantId = isMasterTenant ? undefined : tenant?.tenant_id;

  // Dedicated SSO deployments (BSC): users sign in via their organisation's
  // identity provider, so "creating a user" means pre-provisioning a profile —
  // no auth account, no temporary password. Emails are restricted to the
  // organisation's domain because only those can complete SSO anyway.
  const ssoDomain = (import.meta.env.VITE_SSO_DOMAIN as string | undefined)?.trim();
  const isSsoDeployment = Boolean(ssoDomain);
  const [formData, setFormData] = useState({
    username: '',
    display_name: '',
    email: '',
    phone: '',
    avatar_url: '',
    role: 'user' as UserRole,
    organisation_role: 'user' as OrganisationRole,
    company_id: preselectedCompanyId,
    unit_id: undefined as string | undefined,
    job_title: '',
    location: '',
  });

  // NOTE: must be computed AFTER formData is declared — referencing formData
  // above its useState() throws a temporal-dead-zone ReferenceError at render,
  // which white-screened/looped the Add User page.
  const emailDomainValid = !isSsoDeployment
    || formData.email.trim() === ''
    || formData.email.trim().toLowerCase().endsWith(`@${ssoDomain!.toLowerCase()}`);

  const [validation, setValidation] = useState({
    usernameChecking: false,
    usernameAvailable: true,
    emailChecking: false,
    emailAvailable: true,
  });

  const [submitting, setSubmitting] = useState(false);

  // Fetch companies (filtered by DOMAIN's tenant for white-label isolation)
  const { data: companies } = useQuery({
    queryKey: ['companies', effectiveTenantId],
    queryFn: () => fetchCompanies({ tenant_id: effectiveTenantId }),
    enabled: isMasterTenant || !!effectiveTenantId, // Don't fetch until we know the tenant
  });

  // Username handling:
  // - Master tenant (MEDD internal): username is a real login credential, so
  //   the admin sees/edits the field (auto-filled from email as a convenience).
  // - White-label / SSO tenants (e.g. Boston): login is via SSO, so the
  //   username is never typed by anyone — we derive it from the person's own
  //   email local-part (which IS their directory username, e.g.
  //   Peter.Thornton2@bsci.com -> peter.thornton2). The field is hidden.
  useEffect(() => {
    if (!formData.email) return;
    if (isMasterTenant && formData.username) return; // don't clobber manual master-tenant edits
    const usernameBase = formData.email.split('@')[0].replace(/[^a-z0-9._-]/gi, '').toLowerCase();
    setFormData((prev) => (prev.username === usernameBase ? prev : { ...prev, username: usernameBase }));
  }, [formData.email, formData.username, isMasterTenant]);

  // Check username availability (debounced)
  useEffect(() => {
    if (!formData.username || formData.username.length < 3) return;

    setValidation((prev) => ({ ...prev, usernameChecking: true }));
    const timer = setTimeout(async () => {
      const available = await checkUsernameAvailable(formData.username);
      setValidation((prev) => ({
        ...prev,
        usernameChecking: false,
        usernameAvailable: available,
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username]);

  // Check email availability (debounced)
  useEffect(() => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) return;

    setValidation((prev) => ({ ...prev, emailChecking: true }));
    const timer = setTimeout(async () => {
      const available = await checkEmailAvailable(formData.email);
      setValidation((prev) => ({
        ...prev,
        emailChecking: false,
        emailAvailable: available,
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.email]);

  // Role options - context-aware based on tenant and user permissions.
  //
  // Mental model (verified against route guards in MainApp.tsx):
  //   Master tenant: 'admin' role = Super Administrator (system-wide)
  //   White-label tenant: 'admin' role = Tenant Admin (broad, this tenant only)
  //   'company_admin' role = Branch Admin (scoped to one branch)
  //   'tenant_admin' UI value = creates super_admin in tenant_admins table
  const roleOptions = [
    // Only show Super Administrator for master tenant (MEDD)
    ...(isMasterTenant ? [{
      value: 'admin' as UserRole,
      label: 'Super Administrator',
      icon: '👑',
      description: 'Full system access - manage all users, agents, companies, and settings',
      masterOnly: true,
    }] : []),
    // Show Tenant Super Admin for white-label tenant super admins
    ...(isTenantSuperAdmin ? [{
      value: 'tenant_admin' as UserRole,
      label: 'Tenant Super Admin',
      icon: '👑',
      description: 'Top-level admin — can manage all users (including other admins) and tenant settings',
      masterOnly: false,
    }] : []),
    // Tenant Admin (plain admin role) for white-label tenants
    ...(!isMasterTenant ? [{
      value: 'admin' as UserRole,
      label: 'Tenant Admin',
      icon: '🛡️',
      description: 'Manage users, agents, and simulations across all branches in this tenant',
      masterOnly: false,
    }] : []),
    {
      value: 'company_admin' as UserRole,
      label: 'Branch Admin',
      icon: '🏢',
      description: 'Manage users, agents, and simulations within their assigned branch (sub-company)',
      masterOnly: false,
    },
    {
      value: 'user' as UserRole,
      label: 'Standard User',
      icon: '👤',
      description: 'Basic access to assigned simulations',
      masterOnly: false,
    },
  ];

  const enterpriseRoleOptions = [
    {
      value: 'company_admin' as OrganisationRole,
      label: ORGANISATION_ROLE_LABELS.company_admin,
      icon: '🌐',
      description: 'Manage every branch, unit, administrator, manager and training asset in this company.',
      masterOnly: false,
      allowedFor: ['company_admin'] as OrganisationRole[],
    },
    {
      value: 'branch_admin' as OrganisationRole,
      label: ORGANISATION_ROLE_LABELS.branch_admin,
      icon: '🏢',
      description: 'Manage units, people and training assets within a branch. Administrators may hold multiple scoped assignments.',
      masterOnly: false,
      allowedFor: ['company_admin'] as OrganisationRole[],
    },
    {
      value: 'unit_admin' as OrganisationRole,
      label: ORGANISATION_ROLE_LABELS.unit_admin,
      icon: '🛡️',
      description: 'Branch Admin-equivalent administration within a unit. Administrators may hold multiple scoped assignments.',
      masterOnly: false,
      allowedFor: ['company_admin', 'branch_admin', 'unit_admin', 'unit_manager'] as OrganisationRole[],
    },
    {
      value: 'unit_manager' as OrganisationRole,
      label: ORGANISATION_ROLE_LABELS.unit_manager,
      icon: '👥',
      description: 'Branch Admin-equivalent administration plus submitted-report review across assigned units.',
      masterOnly: false,
      allowedFor: ['company_admin', 'branch_admin', 'unit_admin', 'unit_manager'] as OrganisationRole[],
    },
    {
      value: 'user' as OrganisationRole,
      label: ORGANISATION_ROLE_LABELS.user,
      icon: '👤',
      description: 'Complete assigned simulations and submit selected results for manager review.',
      masterOnly: false,
      allowedFor: ['company_admin', 'branch_admin', 'unit_admin', 'unit_manager'] as OrganisationRole[],
    },
  ].filter((option) => effectiveRole && option.allowedFor.includes(effectiveRole));

  const displayedRoleOptions = hasEnterpriseHierarchy ? enterpriseRoleOptions : roleOptions;
  const selectedRole = hasEnterpriseHierarchy ? formData.organisation_role : formData.role;
  const branchCompanies = hasEnterpriseHierarchy
    ? companies?.filter((company: any) => company.parent_company_id)
    : companies;
  const selectedBranchUnits = organisationUnits.filter((unit) => unit.branch_id === formData.company_id);

  useEffect(() => {
    if (!hasEnterpriseHierarchy) return;
    setFormData((prev) => ({
      ...prev,
      company_id: prev.company_id || activeAssignment?.branch_id || undefined,
      unit_id: prev.unit_id || activeUnitId || undefined,
    }));
  }, [activeAssignment?.branch_id, activeUnitId, hasEnterpriseHierarchy]);

  // White-label/SSO tenants (BSC) are branch-based: every user must belong to a
  // company/branch — "None (Independent)" is only meaningful for master MEDD.
  // Company Admins always need one too.
  const companyRequired = hasEnterpriseHierarchy
    ? formData.organisation_role !== 'company_admin'
    : !isMasterTenant || formData.role === 'company_admin';
  const requiresCompany = companyRequired && !formData.company_id;
  const unitRequired = hasEnterpriseHierarchy
    && ['unit_admin', 'unit_manager', 'user'].includes(formData.organisation_role);
  const requiresUnit = unitRequired && !formData.unit_id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.display_name.trim() || formData.display_name.length < 2) {
      return;
    }

    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      return;
    }

    if (!formData.username.trim() || formData.username.length < 3) {
      return;
    }

    // On white-label tenants, skip the blocking availability check.
    // The edge function (create-tenant-admin) handles all three cases:
    //   - Brand-new email → creates auth user with temp password
    //   - Existing email, never signed in → resets password to fresh temp (recovery case)
    //   - Existing email, actively used → reuses, sends recovery-only email
    // For master tenant we keep the strict check (SSO/staging creation path
    // cannot currently recover existing users).
    if (isMasterTenant && (!validation.usernameAvailable || !validation.emailAvailable)) {
      return;
    }

    // SSO deployments: only organisation emails can complete SSO — reject
    // anything else with a specific message rather than failing at login.
    if (isSsoDeployment && !formData.email.trim().toLowerCase().endsWith(`@${ssoDomain!.toLowerCase()}`)) {
      toast.error(`Only @${ssoDomain} email addresses can be added — this platform signs in through your organisation's SSO.`);
      return;
    }

    // Branch-based tenants: every user must belong to a company/branch.
    if (companyRequired && !formData.company_id) {
      toast.error(isMasterTenant ? 'Please select a company for this user.' : 'Please select the branch this user belongs to.');
      return;
    }

    if (unitRequired && !formData.unit_id) {
      toast.error('Please select the unit this person will join.');
      return;
    }

    setSubmitting(true);

    if (hasEnterpriseHierarchy) {
      const enterpriseDb = supabase as any;
      const { data: profileId, error } = await enterpriseDb.rpc('provision_enterprise_profile_with_avatar', {
        p_email: formData.email,
        p_display_name: formData.display_name,
        p_role: formData.organisation_role,
        p_branch_id: formData.organisation_role === 'company_admin' ? null : formData.company_id || null,
        p_unit_id: unitRequired ? formData.unit_id || null : null,
        p_job_title: formData.job_title || null,
        p_phone: formData.phone || null,
        p_avatar_url: formData.avatar_url || null,
        p_is_primary: true,
      });

      if (error || !profileId) {
        toast.error(error?.message || 'Unable to provision this organisation user.');
        setSubmitting(false);
        return;
      }

      const selectedCompany = branchCompanies?.find((company: any) => company.id === formData.company_id);
      const selectedUnit = organisationUnits.find((unit) => unit.id === formData.unit_id);

      if (isSsoDeployment) {
        // SSO tenants (Boston): the identity provider creates the auth account
        // at first sign-in, so the welcome email is deliberately passwordless.
        supabase.functions.invoke('send-welcome-email', {
          body: {
            email: formData.email,
            name: formData.display_name,
            company_name: selectedCompany?.name,
            role: formData.organisation_role,
            unit_name: selectedUnit?.name,
          },
        }).catch((err) => console.warn('Welcome email dispatch failed:', err));

        toast.success(`${formData.display_name} added as ${ORGANISATION_ROLE_LABELS[formData.organisation_role]}.`);
        setSubmitting(false);
        navigate('/admin/users');
        return;
      }

      // Password-auth tenants: provisioning above created the profile and the
      // organisation role, but no way to sign in. tenant-invite-user creates
      // (or reuses) the auth account, generates a temporary password for a
      // brand-new user, links the auth identity onto the profile provisioning
      // just created, and sends the credentialed invite email. Without this
      // call the person receives a welcome email and no account exists at all
      // — reported by three tenants on 2026-09-04 (Lara Pham, AstraZeneca,
      // had a profile, no auth user, and no working Forgot Password).
      const { data: inviteResult, error: inviteError } = await supabase.functions.invoke('tenant-invite-user', {
        body: {
          email: formData.email,
          display_name: formData.display_name,
          // Enterprise mode: the function validates this exact profile and
          // derives the owning company and legacy role server-side from its
          // organisation assignment. Passing a client-side company here broke
          // company_admin (no branch selected → empty company_id → refused),
          // and a client-side role mapping downgraded company_admin to
          // legacy company_admin instead of admin.
          profile_id: profileId,
          role: 'user',
          redirect_to: `${window.location.origin}/reset-password`,
        },
      });
      if (inviteError || inviteResult?.success === false) {
        // The organisation profile exists; only the sign-in half failed. Say so
        // precisely, because "user added" here would repeat the original bug.
        toast.error(
          inviteResult?.error || inviteError?.message
            ? `Profile created, but login credentials could not be issued: ${inviteResult?.error || inviteError?.message}. Use "Resend welcome email" to retry.`
            : 'Profile created, but login credentials could not be issued. Use "Resend welcome email" to retry.',
        );
        setSubmitting(false);
        return;
      }

      toast.success(`${formData.display_name} added as ${ORGANISATION_ROLE_LABELS[formData.organisation_role]} — login details emailed.`);
      setSubmitting(false);
      navigate('/admin/users');
      return;
    }

    if (isSsoDeployment) {
      // Pre-provision a profile only. No auth account and no temporary
      // password — SSO creates the auth user at first login, and
      // sync-sso-user links this profile + applies the pending role.
      const selectedCompany = companies?.find((c: { id: string; name: string }) => c.id === formData.company_id);
      const result = await createOrLinkProfile({
        email: formData.email,
        display_name: formData.display_name,
        company_id: formData.company_id || undefined,
        company_name: selectedCompany?.name,
        role: (formData.role === 'tenant_admin' ? 'admin' : formData.role) as 'admin' | 'company_admin' | 'user',
        created_by: currentUser?.name,
        tenant_id: effectiveTenantId,
        job_title: formData.job_title,
        phone: formData.phone,
      });

      if (result.success) {
        // Branded invite email — deliberately passwordless (SSO sign-in only).
        supabase.functions.invoke('send-welcome-email', {
          body: {
            email: formData.email,
            name: formData.display_name,
            company_name: selectedCompany?.name,
            role: formData.role,
          },
        }).catch((err) => console.warn('Welcome email dispatch failed:', err));

        toast.success(`${formData.display_name} added — they can now sign in with their organisation account.`);
        setSubmitting(false);
        navigate('/admin/users');
        return;
      }

      toast.error(result.error || 'Failed to add user');
      setSubmitting(false);
      return;
    }

    const result = await createUser({
      username: formData.username,
      display_name: formData.display_name,
      email: formData.email,
      phone: formData.phone,
      avatar_url: formData.avatar_url,
      company_id: formData.company_id || undefined,
      job_title: formData.job_title,
      location: formData.location,
      role: formData.role,
      created_by: currentUser?.name,
      tenant_id: currentUser?.tenant_id, // Associate with current user's tenant
    });

    setSubmitting(false);

    if (result.success) {
      navigate('/admin/users');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Users
        </button>
        <h1 className="text-3xl font-bold text-foreground">Create New User</h1>
        <p className="text-muted-foreground mt-1">Add a new user account and assign roles</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-background border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="display_name">Display Name *</Label>
              <Input
                id="display_name"
                required
                value={formData.display_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                placeholder="John Smith"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <div className="relative mt-2">
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder={isSsoDeployment ? `first.last@${ssoDomain}` : 'john.smith@example.com'}
                  className={!emailDomainValid ? 'border-red-500 focus-visible:ring-red-500' : undefined}
                />
                {validation.emailChecking && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    Checking...
                  </span>
                )}
                {!validation.emailChecking && formData.email && validation.emailAvailable && emailDomainValid && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600" size={18} />
                )}
                {!validation.emailChecking && formData.email && (!validation.emailAvailable || !emailDomainValid) && (
                  <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-600" size={18} />
                )}
              </div>
              {!emailDomainValid && (
                <p className="text-xs text-red-600 mt-1">
                  Only @{ssoDomain} email addresses can be added — users sign in through your organisation's SSO, so other addresses would never be able to log in.
                </p>
              )}
              {!validation.emailAvailable && formData.email && (
                isSsoDeployment ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-1">
                    ℹ️ This email is already on the platform. Submitting will update their company and role — no password is involved, they sign in with their organisation account.
                  </p>
                ) : isMasterTenant ? (
                  <p className="text-xs text-red-600 mt-1">This email is already registered</p>
                ) : (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-1">
                    ℹ️ A MEDD account with this email already exists. Submitting will invite them to this tenant — if they've never logged in, we'll reset their password and send a fresh temp password. If they're already using MEDD elsewhere, they'll get a "sign in with existing password" email.
                  </p>
                )
              )}
            </div>

            {/* Username: only master-tenant (MEDD) admins set it manually — it's a
                login credential there. For SSO/white-label tenants (Boston) the
                username is derived automatically from the person's email and the
                field is hidden, so no one has to type it. */}
            {isMasterTenant && (
              <div>
                <Label htmlFor="username">Username *</Label>
                <div className="relative mt-2">
                  <Input
                    id="username"
                    required
                    value={formData.username}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') }))
                    }
                    placeholder="john.smith"
                    className="font-mono"
                  />
                  {validation.usernameChecking && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      Checking...
                    </span>
                  )}
                  {!validation.usernameChecking && formData.username && validation.usernameAvailable && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600" size={18} />
                  )}
                  {!validation.usernameChecking && formData.username && !validation.usernameAvailable && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-600" size={18} />
                  )}
                </div>
                {!validation.usernameAvailable && formData.username && (
                  <p className="text-xs text-red-600 mt-1">Username already taken</p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="+61 4XX XXX XXX"
                className="mt-2"
              />
            </div>
          </div>
        </div>

        {/* Role & Company */}
        <div className="bg-background border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Role & Permissions</h2>
          <div className="grid grid-cols-1 gap-4 mb-6">
            {displayedRoleOptions.map((role) => (
              <button
                key={role.value}
                type="button"
                onClick={() => setFormData((prev) => hasEnterpriseHierarchy
                  ? {
                      ...prev,
                      organisation_role: role.value as OrganisationRole,
                      company_id: role.value === 'company_admin' ? undefined : prev.company_id,
                      unit_id: ['unit_admin', 'unit_manager', 'user'].includes(role.value) ? prev.unit_id : undefined,
                    }
                  : { ...prev, role: role.value as UserRole })}
                className={`p-4 border-2 rounded-xl transition-all text-left ${
                  selectedRole === role.value
                    ? 'border-primary bg-primary/5'
                    : role.masterOnly
                      ? 'border-amber-500/50 bg-amber-500/5 hover:border-amber-500'
                      : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{role.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground flex items-center gap-2">
                      {role.label}
                      {role.masterOnly && (
                        <span className="text-xs bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-full font-medium">
                          System Level
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{role.description}</div>
                  </div>
                  {selectedRole === role.value && <CheckCircle className="text-primary" size={24} />}
                </div>
              </button>
            ))}
          </div>

          {requiresCompany && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                ⚠️ {isMasterTenant ? 'Company Admin role requires a company to be selected' : 'Please select the branch this user belongs to'}
              </p>
            </div>
          )}

          {requiresUnit && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                ⚠️ Please select the unit this person will join.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(!hasEnterpriseHierarchy || formData.organisation_role !== 'company_admin') && <div>
              <Label htmlFor="company">
                {isMasterTenant ? 'Company' : 'Branch'} {companyRequired && <span className="text-destructive">*</span>}
              </Label>
              <Select
                value={formData.company_id}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, company_id: value, unit_id: undefined }))}
              >
                <SelectTrigger className={`mt-2 ${requiresCompany ? 'border-amber-500' : ''}`}>
                  <SelectValue placeholder={isMasterTenant ? 'None (Independent)' : 'Select a branch'} />
                </SelectTrigger>
                <SelectContent>
                  {branchCompanies?.map((company: any) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>}

            {unitRequired && (
              <div>
                <Label htmlFor="unit">Unit <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.unit_id}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, unit_id: value }))}
                  disabled={!formData.company_id}
                >
                  <SelectTrigger className={`mt-2 ${requiresUnit ? 'border-amber-500' : ''}`}>
                    <SelectValue placeholder="Select a unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedBranchUnits.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="job_title">Job Title</Label>
              <Input
                id="job_title"
                value={formData.job_title}
                onChange={(e) => setFormData((prev) => ({ ...prev, job_title: e.target.value }))}
                placeholder="Medical Sales Representative"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="Sydney, NSW"
                className="mt-2"
              />
            </div>
          </div>
        </div>

        {/* Avatar */}
        <div className="bg-background border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Avatar</h2>
          <ImageUpload
            value={formData.avatar_url || ''}
            onChange={(url) => setFormData((prev) => ({ ...prev, avatar_url: url }))}
            pathPrefix="avatars"
            placeholder="user"
            shape="circle"
            label="Profile Photo"
          />
        </div>

        {/* Form Actions */}
        <div className="flex gap-4">
          <Button
            type="submit"
            className="flex-1"
            disabled={
              submitting ||
              validation.usernameChecking ||
              validation.emailChecking ||
              requiresCompany ||
              requiresUnit ||
              // On master tenant, keep strict guard (SSO/staging flow can't recover existing users).
              // On white-label tenants, allow submit even if email/username are "taken" —
              // the edge function handles existing-user invite/recovery correctly.
              (isMasterTenant && (!validation.usernameAvailable || !validation.emailAvailable))
            }
          >
            {submitting
              ? 'Creating...'
              : !isMasterTenant && (!validation.emailAvailable || !validation.usernameAvailable)
                ? 'Invite Existing User'
                : 'Create User'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/users')} className="flex-1">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
